import { getGeoCell } from '../../src/services/analytics/deviceLocation';

export interface eventProps {
  action: string;
  accountId?: string;
  storyId?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  userId?: string;
  email?: string;
  platform?: string;
  location?: string;
  storyUrl?: string;
  phoneNumber?: string;
  form?: string;
  tos?: boolean;
  verificationCode?: string;
  page?: string;
  fileSize?: number;
  errorMessage?: string;
  campaignId?: string | null;
  source?: string;
  url?: string;
  token?: string | null;
  endpoint?: string;
  env?: string;
  time?: number;
  speedMbps?: number;
  speedMbpsEwma?: number;
  uploadTimeSeconds?: number;
  chunkSize?: number;
  effectiveType?: string;
  uaFamily?: string;
  saveData?: boolean;
  etaSeconds?: number;
  mode?: string;
  videoId?: string | null;
  creatorId?: string | null;
  size?: number;
  value?: string | number;
  event_timestamp?: string;
  attempt?: number;
  /** ms a chunk PUT may stall (no upload progress) before being aborted. */
  stallTimeoutMs?: number;
  /** ms the upload had no forward progress when the stall watchdog fired. */
  stalledForMs?: number;
  /** Byte offset in the file at which the affected part began. */
  chunkOffset?: number;
  /** Bytes the browser had handed to the socket when the part stalled. */
  loadedBytes?: number;
  /** True when this retry follows a stall (vs. a plain network error). */
  stalled?: boolean;
  /** Part size (bytes) after the post-stall shrink; null when not shrunk. */
  shrunkToBytes?: number | null;
  /** Server-confirmed offset the retry resumed from; null when not resumed. */
  resumedFromOffset?: number | null;
  /** Bytes the 308 probe let us skip re-uploading on this retry. */
  skippedBytes?: number;
  /** True when the stall raced a completing PUT and the part was already committed. */
  chunkFullyCommitted?: boolean;
  /** Measured serialized server-ack wait for this part (ms); null when no progress event fired. */
  ackMs?: number | null;
  /** Full wall time of this part's PUT (ms) — transfer + ack. */
  partDurationMs?: number;
  /** GA4 transport hint; 'beacon' for events fired on page leave. */
  transport_type?: 'beacon';
  /** Tracking spec v2 fields are snake_case and typed at the call site. */
  [v2Field: string]: string | number | boolean | null | undefined;
}
// GA4 EVENT TRACK
// gtag is defined by the GA snippet, which can load after the app's first
// effects run. Calling it before then threw and took the whole page down, so
// events (and user properties) wait in a queue and go out in order once gtag
// exists. Analytics must never break the page: every call is guarded.
type GtagArgs = [string, string, Record<string, unknown>?];
const gtagQueue: GtagArgs[] = [];
let gtagFlushTimer: ReturnType<typeof setInterval> | null = null;
const gtagReady = () => typeof window !== 'undefined' && typeof window.gtag === 'function';
const flushGtagQueue = () => {
  if (!gtagReady()) return false;
  while (gtagQueue.length) {
    const args = gtagQueue.shift() as GtagArgs;
    try {
      window.gtag(...args);
    } catch {
      /* never let analytics throw */
    }
  }
  return true;
};
export const callGtag = (...args: GtagArgs): void => {
  if (typeof window === 'undefined') return;
  gtagQueue.push(args);
  if (flushGtagQueue()) return;
  if (gtagFlushTimer) return;
  const startedAt = Date.now();
  gtagFlushTimer = setInterval(() => {
    if (flushGtagQueue() || Date.now() - startedAt > 30_000) {
      if (gtagFlushTimer) clearInterval(gtagFlushTimer);
      gtagFlushTimer = null;
    }
  }, 100);
};

const trackGAEvent: (arg0: eventProps) => void = ({ action, ...props }) => {
  callGtag('event', action, {
    ...(props.storyId ? { video_id: props.storyId } : {}),
    ...props,
  });
};
// GLOBAL EVENT TRACK
const testENVS = ['development', 'staging', 'demo'];
const env = process.env.APP_ENV || '';

// Personal data never leaves the browser through analytics. These keys are
// stripped from every event regardless of which screen sends them.
const PII_KEYS = ['email', 'phoneNumber', 'firstName', 'lastName', 'token', 'verificationCode'] as const;

// GA4 limits: 25 parameters per event (extra ones are dropped server-side,
// invisibly), 25 user properties, 36-character user-property values.
const GA4_MAX_EVENT_PARAMS = 25;
let lastUserProps = '';

/** Session-level values as GA4 user properties. Sent only when they change. */
export const setAnalyticsUserProperties = (props: Record<string, string>): void => {
  if (typeof window === 'undefined') return;
  const key = JSON.stringify(props);
  if (key === lastUserProps) return;
  lastUserProps = key;
  callGtag('set', 'user_properties', props);
};

export const trackEvent: (arg0: eventProps & { v2?: boolean }) => void = ({ action, accountId, v2, ...rawProps }) => {
  const props: Partial<eventProps> = { ...rawProps };
  for (const key of PII_KEYS) delete props[key];
  // Absent means null: unset fields would only spend the parameter budget.
  Object.keys(props).forEach((k) => {
    if (props[k] === null || props[k] === undefined) delete props[k];
  });
  // Legacy events keep the params existing dashboards were built on.
  const legacy = v2 ? {} : { env: process.env.APP_ENV, event_timestamp: new Date().toISOString() };
  // gtag sends a user property only on the first event after it changes, so
  // the location cell (device_geo) also rides on every event once captured.
  const geoCell = getGeoCell();
  if (geoCell && props.geo_cell === undefined) props.geo_cell = geoCell;
  const formattedAction = action.replace('-> ', '').replace(/ /g, '_');
  const payload = { ...(accountId ? { accountId } : {}), ...legacy, ...props };
  const paramCount = Object.keys(payload).length + (props.storyId ? 1 : 0);
  if (testENVS.includes(env)) {
    console.log(`%c tracked ${action}`, 'background: #222; color: yellow', props);
    if (paramCount > GA4_MAX_EVENT_PARAMS) {
      console.warn(`[analytics] ${formattedAction} sends ${paramCount} params; GA4 keeps ${GA4_MAX_EVENT_PARAMS}.`);
    }
  }
  trackGAEvent({ action: formattedAction, ...payload });
};
